'use client'

import { useSelector } from 'react-redux'

import PersonalizationBanner from 'components/PersonalizationBanner'
import PortalDraftItemReminderBanner from 'components/PortalDraftItemReminderBanner'
import { getBanners } from 'state/banners/selectors'
import { ClickableElement } from 'constants/tracking/clickable-elements'
import { clickEvent } from '_libs/common/event-tracker/events'
import useTracking from 'hooks/useTracking'
import useFetchClosets from 'hooks/useFetchClosetPromotions'
import { ResponseError } from 'types/api/response'

import BlockTitle from '../common/BlockTitle'
import DynamicSpacer from '../common/DynamicSpacer'
import ItemsLoader from './components/ItemsLoader'
import ItemsGrid from './components/ItemsGrid'
import useFetchItems from './hooks/useFetchFeedItems'
import useTabs from '../hooks/useTabs'

type Props = {
  onEmpty: (reason: ResponseError) => void
}

const Feed = ({ onEmpty }: Props) => {
  const banners = useSelector(getBanners)

  const { currentTab } = useTabs()
  const { track } = useTracking()

  const { events, endReached, isLoading, showLoadMoreButton, fetchFeedEvents, isFirstPageFetched } =
    useFetchItems({
      onEmpty,
    })
  const { closets, fetchClosetPromo } = useFetchClosets({
    isEnabled: currentTab.feed.arePromotedClosetsEnabled,
  })

  function handleLoadMoreButtonClick() {
    track(clickEvent({ target: ClickableElement.FeedLoadMoreButton }))

    fetchFeedEvents()
    fetchClosetPromo(events.length)
  }

  async function handlePageEnd() {
    const isFirstFetch = !isFirstPageFetched
    const newItemCount = await fetchFeedEvents()

    if (isFirstFetch && newItemCount) {
      fetchClosetPromo(newItemCount)
    }
  }

  function renderPersonalizationBanner() {
    if (!endReached || !banners.feedPersonalizationBanner) return null

    return <PersonalizationBanner banner={banners.feedPersonalizationBanner} />
  }

  function renderPortalDraftReminderBanner() {
    if (!banners.portalMergeDraftItemsReminder) return null

    return <PortalDraftItemReminderBanner banner={banners.portalMergeDraftItemsReminder} />
  }

  return (
    <>
      {isFirstPageFetched && (
        <>
          <BlockTitle title={currentTab.feed.title} />
          <DynamicSpacer phones="Regular" tabletsUp="Large" />
        </>
      )}
      <ItemsGrid events={events} closets={closets} />
      <ItemsLoader
        endReached={endReached}
        isLoading={isLoading}
        showLoadMoreButton={showLoadMoreButton}
        handleLoadMoreButtonClick={handleLoadMoreButtonClick}
        onPageEnd={handlePageEnd}
      />
      {renderPortalDraftReminderBanner()}
      {renderPersonalizationBanner()}
    </>
  )
}

export default Feed
